// MA-702 — the dot-group OPTION-TILE layout vocabulary: the one place that
// says which authored layout names the option-tile renderer can draw, and
// how many dots each one draws.
//
// WHY: `ms.math.00e`'s "Match the card to the right group of dots" items
// (FI_NUM_00e_019-030) author a per-option dot layout
// (`sourceStimulus_v2.answerOptions = {kind: "dot_groups", optionLayouts:
// {"0": "empty_circle", "1": "single_center_dot", ...}}`) and the dark
// `ms.math.00c` "Tap the picture that matches the number" items
// (FI_NUM_00a_016-025) author `quantityChoices: {elementKind: "dots",
// arrangement: "five_frame_aligned"}`. Nothing read either spec, so the
// tiles served four printed digits. The mapper (contentToEngineItem.ts,
// `optionDotGroupsFor`) and the tile renderer (DotPatternRenderer.tsx,
// `DotGroupGlyph`) BOTH read this table, so the set of layouts the mapper
// admits can never drift from the set the renderer can draw.
//
// A layout is admitted for an option ONLY when its fixed dot count equals
// the option's own count (or, for the frame layout, the count fits the
// frame). A layout name not in this table (e.g. the dark ms.math.00d
// `ten_frame_aligned`, counts 6-10) is not drawable here, so the mapper
// fails closed for that record and the tiles stay numerals.

/** Layouts with a FIXED dot count (one drawing per name). Every layout is
 *  drawn inside the same outline "plate"; `empty_circle` (the authored name
 *  for zero) is drawn as the EMPTY plate — no circle (MA-702 RED round 1). */
export const FIXED_COUNT_DOT_GROUP_LAYOUTS = {
  empty_circle: 0,
  single_center_dot: 1,
  horizontal_pair: 2,
  small_triangle: 3,
  four_corners: 4,
  five_quincunx: 5,
} as const;

/** Layouts that draw any count in a range (a 1x5 frame, first n cells filled). */
export const RANGE_DOT_GROUP_LAYOUTS = {
  five_frame_aligned: { min: 0, max: 5 },
} as const;

export type DotGroupLayout =
  | keyof typeof FIXED_COUNT_DOT_GROUP_LAYOUTS
  | keyof typeof RANGE_DOT_GROUP_LAYOUTS;

/** One option tile's dot group: exactly `count` dots drawn in `layout`. */
export interface OptionDotGroup {
  count: number;
  layout: DotGroupLayout;
}

/** True when `layout` is a drawable layout name AND it draws exactly
 *  `count` dots. Pure. */
export function dotGroupLayoutAdmits(layout: unknown, count: number): layout is DotGroupLayout {
  if (typeof layout !== "string" || !Number.isInteger(count) || count < 0) return false;
  if (Object.prototype.hasOwnProperty.call(FIXED_COUNT_DOT_GROUP_LAYOUTS, layout)) {
    return FIXED_COUNT_DOT_GROUP_LAYOUTS[layout as keyof typeof FIXED_COUNT_DOT_GROUP_LAYOUTS] === count;
  }
  if (Object.prototype.hasOwnProperty.call(RANGE_DOT_GROUP_LAYOUTS, layout)) {
    const r = RANGE_DOT_GROUP_LAYOUTS[layout as keyof typeof RANGE_DOT_GROUP_LAYOUTS];
    return count >= r.min && count <= r.max;
  }
  return false;
}

/** The accessible name of the option tile at `index` (0-based, display
 *  order): "dot group A", "dot group B", ... — distinct per tile, never a
 *  numeral or a number word, never the count (the task IS mapping the
 *  card's numeral to a quantity, so a spoken/announced count would hand
 *  the child the match). Pure. */
export function dotGroupTileLabel(index: number): string {
  return `dot group ${String.fromCharCode(65 + index)}`;
}

/** The dot group an option tile draws, or undefined when the tile shows its
 *  digit. ChoiceTiles reads each tile's dot group through THIS lookup, and
 *  MA-728's probe summary below reads the same lookup, so the two can
 *  never disagree about which tiles are dot tiles. Pure. */
export function optionDotGroupFor(
  optionDotGroups: Record<string, OptionDotGroup> | undefined,
  option: string,
): OptionDotGroup | undefined {
  return optionDotGroups?.[option];
}

/** MA-728 (capture instrument for MA-702) — which of an item's answer tiles
 *  draw a dot group, and the accessible names those tiles carry: the count of
 *  options with a dot group and their `dotGroupTileLabel` names, in display
 *  order. Walks the options exactly as ChoiceTiles does (same order, same
 *  index, same `optionDotGroupFor` lookup). Pure. */
export function optionDotGroupTileSummary(
  options: readonly string[] | undefined,
  optionDotGroups: Record<string, OptionDotGroup> | undefined,
): { count: number; labels: string[] } {
  const labels: string[] = [];
  (options ?? []).forEach((option, optionIndex) => {
    if (optionDotGroupFor(optionDotGroups, option)) labels.push(dotGroupTileLabel(optionIndex));
  });
  return { count: labels.length, labels };
}

/** MA-728 — true when every dot-group tile name is different from every
 *  other one and none of them contains a digit. Pure. */
export function dotGroupTileLabelsDistinctAndDigitFree(labels: readonly string[]): boolean {
  return new Set(labels).size === labels.length && labels.every((label) => !/\d/.test(label));
}
